import React, { useEffect, useState } from "react";
import { Moon, Sun, Sunrise } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getGreeting } from "../../utils/getGreeting";
import "./GreetingHeader.css";

const getAppointmentStart = (appointment) => {
  if (appointment?.startTime) return new Date(appointment.startTime);
  if (appointment?.dateTime) return new Date(appointment.dateTime);
  if (appointment?.date) {
    const time = appointment.time || "00:00";
    return new Date(`${appointment.date}T${time}`);
  }
  return null;
};

export default function GreetingHeader({
  doctorName,
  appointments = [],
  animateWelcome = false,
}) {
  const { t } = useTranslation("cabinetDashboard");
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(interval);
  }, []);

  const period = getGreeting(now);
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const todayAppointments = appointments
    .map((appointment) => ({ appointment, start: getAppointmentStart(appointment) }))
    .filter(({ appointment, start }) =>
      start &&
      !Number.isNaN(start.getTime()) &&
      appointment?.source !== "event" &&
      !["cancelled", "canceled"].includes(String(appointment?.status || "").toLowerCase()) &&
      `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}` === todayKey
    )
    .sort((a, b) => a.start - b.start);
  const nextAppointment = todayAppointments.find(({ start }) => start >= now);
  const locale = t("dashboard.locale", { defaultValue: "fr-FR" });
  const timeLabel = nextAppointment?.start.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const displayName = doctorName?.trim() || t("dashboard.greeting.doctorFallback");
  const GreetingIcon = period === "morning" ? Sunrise : period === "afternoon" ? Sun : Moon;

  let context;
  if (todayAppointments.length === 0) {
    context = t("dashboard.greeting.none");
  } else if (nextAppointment) {
    context = t("dashboard.greeting.next", {
      count: todayAppointments.length,
      time: timeLabel,
    });
  } else {
    context = t("dashboard.greeting.done", { count: todayAppointments.length });
  }

  return (
    <header
      className={`greeting-header greeting-header--${period}${animateWelcome ? " greeting-header--welcome" : ""}`}
      aria-label={t("dashboard.greeting.headerLabel")}
    >
      <div className="greeting-header__content">
        <div className="greeting-header__title-row">
          <GreetingIcon aria-hidden="true" className="greeting-header__icon" />
          <h1 className="greeting-header__title">
            {t(`dashboard.greeting.${period}`)}, Dr. {displayName} 👋
          </h1>
        </div>
        <p className="greeting-header__context">{context}</p>
      </div>
    </header>
  );
}
